import type { Channel } from "phoenix";
import { createEffect, untrack } from "solid-js";
import { refreshAliases } from "./aliasList";
import { assertNever, type QueryWindowEntry, type WhoisExtraLine, type WireUserEvent } from "./api";
import { loadArchive } from "./archive";
import { clearLocalAuth, socketUserName, token } from "./auth";
import { applyAutoAwayDebounceFromWire, applyAwayNickSuffixFromWire } from "./autoAway";
import { setAwayState } from "./awayStatus";
import { setBanlistBundle } from "./banlistCard";
import { setServerBundleHash, setServerBundleVersion } from "./bundleHash";
import { casemappingForSlug } from "./casemapping";
import { onDirectoryComplete, onDirectoryFailed, onDirectoryProgress } from "./channelDirectory";
import { channelKey } from "./channelKey";
import { holdDccOffer, resolveDccOffer } from "./dccOffers";
import { diagPush } from "./diagLog";
import { catchUpPairings, deliverPairingEvent } from "./e2e/pairing";
import { deliverPairingOffer, pairingOfferEnded } from "./e2e/pairingOffer";
import { dropE2eRequest, putE2eRequest, reloadE2eRequests } from "./e2eRequests";
import {
  drainE2e,
  drainEveryNetwork,
  encryptedPeerAbsent,
  encryptedSendDropped,
} from "./encryptedChats";
import { setSeveredForFlood } from "./floodSever";
import { refreshHighlights } from "./highlightList";
import { patchHomeNetwork } from "./home";
import { seedIdentity } from "./identity";
import { appendInviteAck } from "./inviteAck";
import { isupportEntryFromWire, seedIsupport } from "./isupport";
import { applyAutoAwayReasonFromWire, applyQuitPartReasonFromWire } from "./leaveReasons";
import { setLinksReply } from "./linksModal";
import { applyLusersBundle, clearLusersRequested } from "./lusersBundle";
import { setMentionsBundle } from "./mentionsWindow";
import { moduleRoot } from "./moduleRoot";
import { setNamesReply } from "./namesModal";
import { mutateNetworkNick, refetchChannels, refetchNetworks, refetchUser } from "./networks";
import { nickEquals } from "./nickEquals";
import { refreshNotificationPrefs } from "./notificationPrefs";
import {
  applyPresenceChange,
  applyPresenceError,
  applyPresenceSnapshot,
  setNotifyList,
} from "./notifyWatch";
import { setPeerAway } from "./peerAway";
import { type QueryWindow, setQueryWindowsByNetwork } from "./queryWindows";
import { railPeer } from "./railPeer";
import { ingestRailWhois } from "./railWhois";
import { clearSeen } from "./reconnectBackfill";
import { setReconnecting } from "./reconnectingStatus";
import { applyRecoverProgress, applyRecoverResult } from "./recoverProgress";
import { purgeScrollback } from "./scrollback";
import {
  clearServerSeedCount,
  noteConnectionState,
  selectedChannel,
  setSelectedChannel,
} from "./selection";
import { setServerReply } from "./serverReplyModal";
import { applyServerSettings } from "./serverSettings";
import { joinUser } from "./socket";
import { seedSupportedUmodes } from "./supportedUmodes";
import { seedUmodes } from "./umodes";
import { MAX_DURATION_SECONDS } from "./videoPolicy";
import { patchWhoisAvatarUrl, setWhoisBundle } from "./whoisCard";
import { setWhoReply } from "./whoModal";
import { setWhowasBundle } from "./whowasCard";
import {
  forceParted,
  setFailed,
  setInvited,
  setJoined,
  setKicked,
  setPending,
} from "./windowState";
import { noteWireDrop } from "./wireDrop";
import { narrowIsupportChanged, narrowWindowStateEvent } from "./wireNarrow";
// #1393 — the arms below narrow through their GENERATED schema instead of a
// hand-written field walk. Both are already emitted; the hand copy was the
// duplicate. Which arms may move is not a judgement call:
// `wireUserBoundary.test.ts` measures hand and schema against the same
// mutation matrix — drop, null, wrong-type AND a value the schema does not
// declare — and against the object each one RETURNS, arm by arm. Only arms
// at parity on both axes are here.
//
// Every arm still narrowed by hand below is one the measurement separated,
// and each carries at its `case` the tolerance the typespec does not: an
// additive field the server may omit, a token set the wire contract forbids
// us to close (#447), a per-field degrade that beats dropping the payload.
// Do not migrate one of those without re-measuring it first.
import {
  S_E2ePairingWireE2ePairingClosedPayload,
  S_E2ePairingWireE2ePairingMessagePayload,
  S_E2ePairingWireE2ePairingOfferedPayload,
  S_E2eRequestsWireE2eRequestGonePayload,
  S_E2eRequestsWireE2eRequestPayload,
  S_NetworksWireConnectionStateEvent,
  S_NetworksWireNetworkAttachedEvent,
  S_NetworksWireNetworkDetachedEvent,
  S_NotifyWireNotifyListPayload,
  S_QueryWindowsWireWindowsListPayload,
  S_ScrollbackWireArchiveChangedPayload,
  S_ScrollbackWireArchivePurgedPayload,
  S_SessionWireAwayConfirmedPayload,
  S_SessionWireBanlistBundlePayload,
  S_SessionWireChannelsChangedPayload,
  S_SessionWireConnectionProgressPayload,
  S_SessionWireDccOfferPayload,
  S_SessionWireDccOfferResolvedPayload,
  S_SessionWireDirectoryCompletePayload,
  S_SessionWireDirectoryFailedPayload,
  S_SessionWireDirectoryProgressPayload,
  S_SessionWireE2eMessagePayload,
  S_SessionWireE2eSendDroppedPayload,
  S_SessionWireInviteAckPayload,
  S_SessionWireLinksBundlePayload,
  S_SessionWireMentionsBundlePayload,
  S_SessionWireNamesReplyPayload,
  S_SessionWireNoSuchNickPayload,
  S_SessionWireOwnNickChangedPayload,
  S_SessionWirePeerAwayPayload,
  S_SessionWirePresenceChangedPayload,
  S_SessionWirePresenceErrorPayload,
  S_SessionWirePresenceSnapshotPayload,
  S_SessionWireServerReplyPayload,
  S_SessionWireSessionIdentityChangedPayload,
  S_SessionWireSupportedUmodesChangedPayload,
  S_SessionWireUmodeChangedPayload,
  S_SessionWireWhoisAvatarReadyPayload,
  S_SessionWireWhoReplyPayload,
  S_SessionWireWhowasBundlePayload,
  S_SessionWireWindowInviteDeclinedPayload,
  S_SessionWireWindowInvitedPayload,
  S_SessionWireWindowPendingPayload,
  S_UserSettingsWireAutoAwayDebounceChangedPayload,
  S_UserSettingsWireAutoAwayReasonChangedPayload,
  S_UserSettingsWireAwayNickSuffixChangedPayload,
  S_UserSettingsWireQuitPartReasonChangedPayload,
} from "./wireSchema";
import type { ServerSettingsWireUploadView } from "./wireTypes";
import { validate } from "./wireValidate";

// Per-user PubSub topic subscriber. Module-singleton side-effect:
// imports for effect, exports nothing public. `main.tsx` imports this
// alongside `subscribe.ts` so the createRoot evaluates at boot.
//
// Wires the server-side `channels_changed` heartbeat (broadcast on
// `Topic.user(user_name)` whenever `Map.keys(Session.Server.state.members)`
// mutates) to a `refetchChannels()` call. The cicchetto `channelsBySlug`
// createResource then re-resolves with the canonical {name, joined,
// source} envelopes from `GET /channels`, and `subscribe.ts`'s
// createEffect re-runs to join WS topics for the new channels.
//
// C1.3: also handles `query_windows_list` push.
//
// CP13: the pre-CP13 `numeric_routed` ephemeral push is gone — server
// numerics now persist as `:notice` rows in their routed window via
// the regular per-channel topic (handled by subscribe.ts), so they
// flow through the same pipeline as PRIVMSG/NOTICE.
//
// CP16 B5: payload typed as `WireUserEvent` discriminated union; the
// dispatch switch ends with `assertNever(payload)` so adding a new
// server-side event kind without a handler arm fails at `tsc` time.
//
// Token-scoped: the join effect tracks `token()` (the sole reactive
// dep; `socketUserName()` reads the persisted subject non-reactively).
// Every token transition rebuilds the socket (socket.ts), so the effect
// leaves the orphaned prior Channel and re-joins on the fresh socket —
// including a rotation that keeps the identity (#364 cicchetto S1).

// #606 — is the rail currently showing the query whose partner is `target`
// on `network`? Used to decide whether a `source: user` whois_bundle (an
// operator /whois) should ALSO refresh the rail card. Issue 2499: an
// encrypted query's rail shows its peer's card too (`railPeer`). `nickEquals`
// folds (#525/#1861) so a case-shift still matches. Read outside any reactive
// scope (a channel event callback), so this is a plain current-value read.
function railIsShowingNick(network: string, target: string): boolean {
  const peer = railPeer();
  return (
    peer?.networkSlug === network && nickEquals(peer.nick, target, casemappingForSlug(network))
  );
}

function parseWindowsMap(raw: Record<string, QueryWindowEntry[]>): Record<number, QueryWindow[]> {
  const result: Record<number, QueryWindow[]> = {};
  for (const [key, val] of Object.entries(raw)) {
    const networkId = Number(key);
    if (!Number.isFinite(networkId) || !Array.isArray(val)) continue;
    result[networkId] = val.map((w) => ({
      targetNick: w.target_nick,
      openedAt: w.opened_at,
    }));
  }
  return result;
}

// #1393d — `isConnectionState` lived here. REV-H H2 wrote it as the runtime
// narrower for `ConnectionState`, and #410 re-sourced its value set from the
// codegen-emitted `NETWORKS_CREDENTIAL_CONNECTION_STATE` so a fourth server
// state would flow through with no hand edit. Its last two readers were the
// `from`/`to`/`connection_state` checks in `connection_state_changed`, and
// that arm now validates against `S_NetworksWireConnectionStateEvent`, whose
// `S_NetworksCredentialConnectionState` node is the same closed set reached
// from the same codegen run. Deleted rather than left in place: an unused
// guard reads as a contract somebody still keeps.

// S15 — exhaustive `Record<Host, true>` over the generated
// `ServerSettingsWireUploadView["active_host"]` closed set so a new
// server host (`Grappa.ServerSettings.upload_host/0` → codegen literal
// union) FAILS tsc here until handled, instead of the
// `server_settings_changed` narrower silently DROPPING the whole
// settings event on the unknown value. NB (#410): `active_host` is a
// struct-FIELD enum, so codegen emits no standalone `as const` array for
// it — this `Record<Host, true>` stays the exhaustiveness guard. Standalone
// leaf enums (message kind, severity, session-log event, …) instead derive
// their runtime Set directly from the generated const (see `wireNarrow.ts`).
type UploadActiveHost = ServerSettingsWireUploadView["active_host"];

const UPLOAD_ACTIVE_HOST_PRESENCE: Record<UploadActiveHost, true> = {
  embedded: true,
  litterbox: true,
};

function isUploadActiveHost(v: unknown): v is UploadActiveHost {
  return typeof v === "string" && Object.hasOwn(UPLOAD_ACTIVE_HOST_PRESENCE, v);
}

// no-silent-drops B6.10 HIGH-11 — per-element narrowers for bundle
// arrays. Pre-fix `narrowUserEvent` checked `Array.isArray(messages)`
// for `mentions_bundle` and `Array.isArray(channels)` for
// `whois_bundle` but did NOT typecheck array elements. A malformed
// element (server bug, proxy mangling, partial response) would slip
// through the boundary and crash a downstream renderer that read a
// missing field. Now each element is narrowed against its wire
// shape; any failure drops the whole bundle (loud, log-once). The
// ones below survive because their arm is still hand-narrowed; the
// rest went with theirs (#1393).

// `whois_bundle.channels` is `string[] | null`. Wire elements arrive
// as IRC mode-prefixed channel names (`@#italia`, `+#grappa`). Reject
// non-string elements; preserve raw strings (cic owns the prefix
// rendering).
function narrowWhoisChannel(raw: unknown): string | null {
  return typeof raw === "string" ? raw : null;
}

// #221 — per-entry narrower for the WHOIS `extra_lines` array. Mirror of
// `Grappa.Session.Wire.whois_extra_line/0` — `{numeric, text}`. A malformed
// element drops the whole bundle (strict, matching narrowWhoisChannel).
function narrowWhoisExtraLine(raw: unknown): WhoisExtraLine | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.numeric !== "number" || typeof r.text !== "string") return null;
  return { numeric: r.numeric, text: r.text };
}

// Maps a per-element narrower across an array; returns the array of
// narrowed values or null if any element fails. Strict — partial
// bundles are server bugs, not graceful-degradation cases.
function narrowArray<T>(raw: unknown, narrow: (el: unknown) => T | null): T[] | null {
  if (!Array.isArray(raw)) return null;
  const out: T[] = [];
  for (const el of raw) {
    const n = narrow(el);
    if (n === null) return null;
    out.push(n);
  }
  return out;
}

// Codebase audit cic M1 — runtime narrowing for WireUserEvent. The
// `WireUserEvent` discriminated union is a TypeScript-side contract;
// it cannot enforce shape at runtime. A malformed server push (kind
// valid but a required field missing or wrong-typed) would let the
// dispatch arm read `undefined` from the payload and either crash
// (`setAwayState(undefined, ...)`) or silently corrupt state. This
// per-arm validator gates the cast: if the shape doesn't match,
// return null and the dispatcher early-returns + logs. Same boundary
// hardening as `Login.tsx`'s `isCaptchaInfo`.
export function narrowUserEvent(raw: unknown): WireUserEvent | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.kind !== "string") return null;
  switch (r.kind) {
    case "channels_changed":
      return validate(S_SessionWireChannelsChangedPayload, r);
    case "query_windows_list":
      // S43 — every entry is validated, not a bare cast: the schema's
      // `{r: {a: …}}` rejects the whole map on one malformed entry.
      return validate(S_QueryWindowsWireWindowsListPayload, r);
    case "mentions_bundle":
      // no-silent-drops B6.10 HIGH-11 — the `messages` elements are narrowed
      // one by one and a malformed element drops the whole bundle, which is
      // what the schema's `{a: <message>}` says; `kind` inside a message is
      // gated on the shared `Message.kind()` closed set the same way, now via
      // the generated `S_ScrollbackMessageKind` rather than a second copy.
      return validate(S_SessionWireMentionsBundlePayload, r);
    case "away_confirmed":
      return validate(S_SessionWireAwayConfirmedPayload, r);
    case "connection_progress":
      // #100 — transient reconnect badge signal. The closed state set is the
      // schema's own `{e: [...]}`, so a malformed value still cannot corrupt
      // the reconnectingByNetwork store.
      return validate(S_SessionWireConnectionProgressPayload, r);
    case "notify_list":
      // #247 — every entry validated, same strictness as query_windows_list.
      return validate(S_NotifyWireNotifyListPayload, r);
    case "presence_changed":
      return validate(S_SessionWirePresenceChangedPayload, r);
    case "presence_error":
      return validate(S_SessionWirePresenceErrorPayload, r);
    case "presence_snapshot":
      // #247 — a value outside the closed presence set drops the whole map,
      // which is the schema's `{r: {e: [...]}}`.
      return validate(S_SessionWirePresenceSnapshotPayload, r);
    case "own_nick_changed":
      return validate(S_SessionWireOwnNickChangedPayload, r);
    case "umode_changed":
      // #229 — per-session umode set. `modes` is a string[] (sorted umode
      // letters, sign stripped); any non-string element drops the whole
      // payload, which is what the schema's `{a: "s"}` already says.
      return validate(S_SessionWireUmodeChangedPayload, r);
    case "session_identity_changed":
      // #388 — the NORMALIZED services-identity verdict. `account` is
      // nullable and display-only; `identified` is the verdict.
      return validate(S_SessionWireSessionIdentityChangedPayload, r);
    case "supported_umodes_changed":
      // #249 — per-session SUPPORTED umode set (mirror of umode_changed).
      // `modes` is the sorted advertised umode letters, and the schema's
      // `{a: "s"}` already drops the whole payload on a non-string element.
      return validate(S_SessionWireSupportedUmodesChangedPayload, r);
    case "isupport_changed":
      // #216 — dual-topic event: this is the LIVE 005 edge on the user
      // topic (the per-channel cold-snapshot goes through the identical
      // `narrowIsupportChanged` in wireNarrow.ts). Shared narrower so the
      // flat CHANMODES/PREFIX shape is validated in exactly one place.
      return narrowIsupportChanged(r);
    case "window_pending":
      return validate(S_SessionWireWindowPendingPayload, r);
    case "window_invited": {
      // #1393d — was hand-narrowed for ONE reason: a `?? "*"` on `inviter`,
      // written for an absent key (#902, older BEAM) and applied to a
      // present null or number as well. `inviter` is a plain `String.t()`
      // that the emitter always fills, and `"*"` is not a neutral filler —
      // it is the server's own statement that the INVITE carried no prefix.
      // Minting it locally puts a fact in the server's mouth. With the
      // tolerance gone the arm IS its schema, so it validates against it.
      const invited = validate(S_SessionWireWindowInvitedPayload, r);
      // The one thing the schema cannot say. It types `inviter` as a free
      // `"s"`, so `""` would pass — and the guard this replaces rejected it.
      // Dropping that check would be a silent strictness LOSS the census
      // cannot see, its mutation matrix never producing an empty string. No
      // IRC nick is empty: `""` is present-and-unusable, the exact class the
      // ruling rejects.
      return invited === null || invited.inviter === "" ? null : invited;
    }
    case "window_invite_declined":
      // #976 — no `state` in the schema either: a declined invite lands in no
      // window state, so the payload carries none (see the server's
      // `window_invite_declined_payload`). Network + channel are the whole
      // contract; a payload missing either names no window and is dropped.
      return validate(S_SessionWireWindowInviteDeclinedPayload, r);
    case "dcc_offer":
      // issue 2089 — the arm IS its schema; nothing here is hand-narrowed.
      // `filename` is already neutralised server-side (control bytes out,
      // length capped, `(unnamed)` for a name that was entirely control
      // bytes), so a `""` cannot reach a renderer and there is no tolerance
      // to re-add — the same reason `window_invited`'s hand narrowing was
      // withdrawn in #1393d, reached from the other direction.
      return validate(S_SessionWireDccOfferPayload, r);
    case "dcc_offer_resolved":
      // The schema pins `resolution` to the closed triple, so a fourth exit
      // invented by a newer server is DROPPED here rather than reaching the
      // store as an unrenderable value. Dropping is the right failure: the
      // banner survives until the cold-subscribe backfill stops re-emitting
      // the offer, which is a stale prompt rather than a wrong action.
      return validate(S_SessionWireDccOfferResolvedPayload, r);
    case "connection_state_changed":
      // REV-J M15: pre-fix this arm carried only the wider transition
      // fields and HomePane patched its row from a separate
      // `home_network_state_changed` event. Folded — the `network`
      // field carries the same `HomeNetworkRow` HomePane consumed
      // before. One logical event, one wire payload, one broadcast.
      //
      // #1393d — forty lines of hand transcription survived only because of
      // ONE nested default: `network.recoverable ?? false` (#581 D2). The
      // census could not even see it until the matrix learned to mutate
      // below the top level, and what it found is that `false` is not a
      // neutral fallback — it is the value that HIDES the /recover button.
      // Withdrawing an affordance is worse than dropping the update: the
      // update recurs on the next transition, the missing button never
      // announces itself. `home_network_row/0` declares `recoverable` a
      // plain `boolean()`, so absence is not a shape any grappa emits.
      //
      // With that gone the arm was measured at full parity with its schema
      // — verdict AND returned value, both censuses — so the transcription
      // goes and the generated schema stands in its place.
      return validate(S_NetworksWireConnectionStateEvent, r);
    case "network_detached":
      // issue 2219 — the arm IS its schema. Three fields, all required,
      // nothing to widen: a payload missing the slug names no network and
      // the only sane reaction is to drop it.
      return validate(S_NetworksWireNetworkDetachedEvent, r);
    case "network_attached":
      // issue 2219 — the arm IS its schema, like its twin above.
      return validate(S_NetworksWireNetworkAttachedEvent, r);
    case "whois_bundle": {
      // C2 — every numeric-derived field is nullable; only network +
      // target are required. is_operator + channels also tolerate
      // missing values (boolean false / null) per Wire.whois_bundle/3
      // shape. Defensive: any malformed shape returns null and the
      // dispatcher logs + drops.
      //
      // P-0a — 11 additional WHOIS-leg flags / strings folded by
      // EventRouter (275/301/307/308/309/310/316/325/326/339/378).
      // Booleans default false on the server when the corresponding
      // numeric did not fire; cic narrows defensively (server bug or
      // legacy mismatch returns null).
      //
      // no-silent-drops B6.10 HIGH-11 — `channels` array elements are
      // now per-element narrowed (string-only). A non-string element
      // drops the whole bundle.
      if (
        typeof r.network !== "string" ||
        typeof r.target !== "string" ||
        (r.user !== null && typeof r.user !== "string") ||
        (r.host !== null && typeof r.host !== "string") ||
        (r.realname !== null && typeof r.realname !== "string") ||
        (r.server !== null && typeof r.server !== "string") ||
        (r.server_info !== null && typeof r.server_info !== "string") ||
        typeof r.is_operator !== "boolean" ||
        // #367 — oper role text: nullable string (bare 313 → null).
        (r.oper_text !== null && typeof r.oper_text !== "string") ||
        (r.idle_seconds !== null && typeof r.idle_seconds !== "number") ||
        (r.signon !== null && typeof r.signon !== "number") ||
        typeof r.using_ssl !== "boolean" ||
        typeof r.is_registered !== "boolean" ||
        typeof r.is_admin !== "boolean" ||
        typeof r.is_services_admin !== "boolean" ||
        typeof r.is_helper !== "boolean" ||
        typeof r.is_chanop !== "boolean" ||
        typeof r.is_agent !== "boolean" ||
        typeof r.is_java !== "boolean" ||
        (r.umodes !== null && typeof r.umodes !== "string") ||
        (r.away_message !== null && typeof r.away_message !== "string") ||
        (r.actually_host !== null && typeof r.actually_host !== "string") ||
        (r.actually_ip !== null && typeof r.actually_ip !== "string") ||
        // #221 — solanum WHOIS-leg fields.
        (r.account !== null && typeof r.account !== "string") ||
        typeof r.secure !== "boolean" ||
        (r.secure_cipher !== null && typeof r.secure_cipher !== "string") ||
        (r.certfp !== null && typeof r.certfp !== "string") ||
        // M3b — additive: absent (pre-M3b server) tolerates same as null.
        (r.avatar_url !== null && r.avatar_url !== undefined && typeof r.avatar_url !== "string")
      )
        return null;
      let channels: string[] | null = null;
      if (r.channels !== null) {
        channels = narrowArray(r.channels, narrowWhoisChannel);
        if (channels === null) return null;
      }
      // #221 — extra_lines: a list of {numeric, text}, or null; a malformed
      // element drops the whole bundle (strict, matching channels above).
      //
      // #1393d — the `!== undefined` half of that guard is gone. It was the
      // ONE tolerance in the whole census with no written reason anywhere:
      // it shipped in the same commit as the field (05551231) and nothing
      // in tree ever said why an absent key should be read as an empty
      // bundle. The typespec says `[whois_extra_line()] | nil` — required
      // and nullable — and `reverse_extra_lines(nil)` really does emit
      // `nil`, so the KEY is always on the wire. `null` therefore stays a
      // legitimate value and is carried as one; a missing key is a shape no
      // grappa produces, and is now treated as such.
      if (!("extra_lines" in r)) return null;
      let extraLines: WhoisExtraLine[] | null = null;
      if (r.extra_lines !== null) {
        extraLines = narrowArray(r.extra_lines, narrowWhoisExtraLine);
        if (extraLines === null) return null;
      }
      return {
        kind: "whois_bundle",
        network: r.network,
        target: r.target,
        // #606 — request origin (add-only). Absent/unknown (pre-#606 server,
        // replayed payload) normalizes to "user" so the /whois card path is
        // the safe default; only an explicit "rail" opts into rail-only
        // routing. Tolerant, NOT a drop condition (old servers omit it).
        source: r.source === "rail" ? "rail" : "user",
        user: r.user as string | null,
        host: r.host as string | null,
        realname: r.realname as string | null,
        server: r.server as string | null,
        server_info: r.server_info as string | null,
        is_operator: r.is_operator,
        oper_text: r.oper_text as string | null,
        idle_seconds: r.idle_seconds as number | null,
        signon: r.signon as number | null,
        channels,
        using_ssl: r.using_ssl,
        is_registered: r.is_registered,
        is_admin: r.is_admin,
        is_services_admin: r.is_services_admin,
        is_helper: r.is_helper,
        is_chanop: r.is_chanop,
        is_agent: r.is_agent,
        is_java: r.is_java,
        umodes: r.umodes as string | null,
        away_message: r.away_message as string | null,
        actually_host: r.actually_host as string | null,
        actually_ip: r.actually_ip as string | null,
        // #221 — solanum WHOIS-leg fields.
        account: r.account as string | null,
        secure: r.secure,
        secure_cipher: r.secure_cipher as string | null,
        certfp: r.certfp as string | null,
        extra_lines: extraLines,
        // M3b — the authenticated `/networks/:id/peer_avatar/:slug` path,
        // never the peer's raw declared URL. `undefined` (pre-M3b server)
        // normalizes to null, same as every other additive nullable field.
        avatar_url: typeof r.avatar_url === "string" ? r.avatar_url : null,
      };
    }
    case "whois_avatar_ready":
      // M3b — incremental patch for a peer avatar fetch that completed
      // AFTER the whois_bundle it belongs to was already sent.
      return validate(S_SessionWireWhoisAvatarReadyPayload, r);
    case "e2e_message":
      // issue 2430 — validated here so a well-formed push is not counted as
      // a wire drop; the dispatch arm below consumes nothing yet.
      return validate(S_SessionWireE2eMessagePayload, r);
    case "e2e_send_dropped":
      // issue 2430 (protocol 46) — a message grappa accepted and then
      // dropped unsent; the dispatch arm below hands it to the E2E client.
      return validate(S_SessionWireE2eSendDroppedPayload, r);
    case "e2e_pairing_message":
      // issue 2430 X1 (protocol 48) — the pairing relay's doorbell.
      return validate(S_E2ePairingWireE2ePairingMessagePayload, r);
    case "e2e_pairing_closed":
      return validate(S_E2ePairingWireE2ePairingClosedPayload, r);
    case "e2e_pairing_offered":
      // issue 2537 (protocol 55) — a pairing offered to the account's devices.
      return validate(S_E2ePairingWireE2ePairingOfferedPayload, r);
    case "e2e_request":
      // issue 2486 (protocol 53) — a request to start an encrypted chat was
      // made or changed state; the dispatch arm below mirrors it.
      return validate(S_E2eRequestsWireE2eRequestPayload, r);
    case "e2e_request_gone":
      return validate(S_E2eRequestsWireE2eRequestGonePayload, r);
    case "names_reply":
      // #140 — /names roster bundle. A malformed member element drops the
      // whole payload rather than rendering a half-typed row, which is the
      // schema's `{a: <member>}`. `narrowMembers` stays in `wireNarrow.ts`:
      // the per-channel `members_seeded` arm still calls it.
      return validate(S_SessionWireNamesReplyPayload, r);
    case "who_reply":
      // #169 — /who roster bundle. A malformed row drops the whole payload
      // rather than rendering a half-typed table. Unlike names_reply this
      // arm was `narrowWhoUsers`'s ONLY caller, so the helper goes with it.
      return validate(S_SessionWireWhoReplyPayload, r);
    case "server_reply":
      // #127/#992 — /info, /version, /motd, /admin reply bundle. A malformed
      // payload drops rather than rendering a half-typed modal.
      //
      // #992 — the `source` discriminant is checked against the closed set
      // ONCE, never a hand-written `!==` chain: that chain was a second,
      // silent copy, and adding `admin` server-side left it rejecting a
      // legitimate reply as "unknown source" with the modal never opening.
      // The membership test was already reading the generated
      // SESSION_WIRE_SERVER_REPLY_SOURCE; the schema's
      // `S_SessionWireServerReplySource` is that same const, so the SSOT is
      // unchanged and only the transcription around it is gone.
      return validate(S_SessionWireServerReplyPayload, r);
    case "invite_ack":
      // P-0e + P-0f — 341 RPL_INVITING ack. Server emits structured
      // (network, channel, peer); cic appends a synthetic row to the
      // per-network store keyed on target channel and renders inline
      // in the $server window scrollback.
      return validate(S_SessionWireInviteAckPayload, r);
    case "bundle_hash": {
      if (typeof r.hash !== "string" || r.hash === "") return null;
      // #292 — version is optional on the wire (omitted when the deployed
      // bundle advertises none). Normalise absent / malformed → null so the
      // consumer always sees `string | null`.
      const version = typeof r.version === "string" && r.version !== "" ? r.version : null;
      return { kind: "bundle_hash", hash: r.hash, version };
    }
    case "server_settings_changed": {
      // UX-6-B2 (2026-05-21) — operator-visible server-settings push.
      // Wire shape mirrors `Grappa.ServerSettings.Wire.server_settings_
      // changed/1` (atoms-out). Per-field narrowing rejects any
      // server bug / proxy mangling at the boundary; a malformed
      // payload drops without corrupting the cache.
      const up = r.upload;
      if (typeof up !== "object" || up === null) return null;
      const u = up as Record<string, unknown>;
      const posInt = (v: unknown): v is number => typeof v === "number" && v > 0;
      if (
        !isUploadActiveHost(u.active_host) ||
        !posInt(u.image_per_file_cap_bytes) ||
        !posInt(u.video_per_file_cap_bytes) ||
        !posInt(u.document_per_file_cap_bytes) ||
        !posInt(u.audio_per_file_cap_bytes) ||
        !posInt(u.global_cap_bytes)
      )
        return null;
      // #201 — LENIENT, unlike the caps above: a pre-#201 server omits
      // the field entirely, and the additive-only wire contract says a
      // client must not choke on a shape it half-recognises. Hard-
      // narrowing it here would drop the WHOLE settings push against an
      // older server and strand the byte caps too. Absent / malformed →
      // the compile-time default, same degrade-don't-drop posture as
      // `http_host_aliases` below.
      const videoMaxDurationSeconds = posInt(u.video_max_duration_seconds)
        ? u.video_max_duration_seconds
        : MAX_DURATION_SECONDS;
      // issue 2175 — the two per-subject ceilings, lenient for the same
      // reason as the duration cap above: a pre-2175 server omits both
      // keys, and hard-narrowing them would drop the WHOLE settings push
      // and strand active_host and every byte cap with it.
      //
      // The strict alternative is not free either. Requiring a field
      // introduced at protocol 26 obliges `MIN_SERVER_PROTOCOL_VERSION`
      // up to 26 (the OBLIGATION in serverProtocol.ts), which would
      // banner every server from 9 to 25 as outdated over two fields
      // this client never reads: the ceilings exist on the wire only so
      // the admin GET reads back what the admin PUT, and
      // `applyServerSettings` drops them on the floor.
      //
      // Absent or malformed → `global_cap_bytes`, which is not a value
      // nobody sent: before the per-subject split the global pool WAS
      // the only ceiling one subject could reach, so it is the true
      // pre-2175 answer rather than a constant invented here.
      const perUserCapBytes = posInt(u.per_user_cap_bytes)
        ? u.per_user_cap_bytes
        : u.global_cap_bytes;
      const perVisitorCapBytes = posInt(u.per_visitor_cap_bytes)
        ? u.per_visitor_cap_bytes
        : u.global_cap_bytes;
      // #324 — deployment HTTP host aliases. Lenient: a malformed /
      // absent value degrades to [] (page origin only) rather than
      // dropping the whole settings push (which would strand the upload
      // caps too). Filter to strings so a proxy-mangled element can't
      // poison mediaLink's host-membership check.
      const httpHostAliases = Array.isArray(r.http_host_aliases)
        ? r.http_host_aliases.filter((h): h is string => typeof h === "string")
        : [];
      // issue 2359 — the auto-away site default. Lenient like the two
      // above: a pre-39 server omits it, and dropping the push over it
      // would strand every upload knob. Absent / malformed → null, which
      // the drawer renders as the bare "use site default". Declared on both
      // registries (`DeliberatelyWidened`, `wireUserBoundary`'s census).
      const aa = r.auto_away as Record<string, unknown> | null | undefined;
      const aaSeconds = typeof aa === "object" && aa !== null ? aa.default_debounce_seconds : null;
      const autoAway =
        typeof aaSeconds === "number" && Number.isInteger(aaSeconds) && aaSeconds >= 0
          ? { default_debounce_seconds: aaSeconds }
          : null;
      return {
        kind: "server_settings_changed",
        upload: {
          active_host: u.active_host,
          image_per_file_cap_bytes: u.image_per_file_cap_bytes,
          video_per_file_cap_bytes: u.video_per_file_cap_bytes,
          document_per_file_cap_bytes: u.document_per_file_cap_bytes,
          audio_per_file_cap_bytes: u.audio_per_file_cap_bytes,
          global_cap_bytes: u.global_cap_bytes,
          per_user_cap_bytes: perUserCapBytes,
          per_visitor_cap_bytes: perVisitorCapBytes,
          video_max_duration_seconds: videoMaxDurationSeconds,
        },
        http_host_aliases: httpHostAliases,
        auto_away: autoAway,
      };
    }
    case "peer_away":
      // P-0b — standalone 301 RPL_AWAY. cic dm-listener routes by
      // `peer:` field; banner renders inline at the top of the
      // peer's DM scrollback when that window is selected.
      return validate(S_SessionWirePeerAwayPayload, r);
    case "no_such_nick":
      // issue 2497 (protocol 54) — a 401 named a nick; the dispatch arm below
      // hands it to the encrypted chats.
      return validate(S_SessionWireNoSuchNickPayload, r);
    case "lusers_bundle": {
      // P-0d — LUSERS bundle. All counts are integer-or-null (253
      // RPL_LUSERUNKNOWN is optional; defensive nullability covers
      // truncated server responses).
      //
      // S44 (codebase review 2026-07-08): dropped the dead `v === null ?
      // null : null` tautology. Per-field null-coercion of a non-number is
      // RETAINED deliberately (not whole-payload reject like the file's
      // state-bearing narrowers): this bundle is a display-only card in the
      // $server window, and a single garbled optional count should render as
      // "—", not blow away the 11 good counts alongside it.
      if (typeof r.network !== "string") return null;
      const intOrNull = (v: unknown): number | null => (typeof v === "number" ? v : null);
      return {
        kind: "lusers_bundle",
        network: r.network,
        total_users: intOrNull(r.total_users),
        invisible: intOrNull(r.invisible),
        servers: intOrNull(r.servers),
        operators: intOrNull(r.operators),
        unknown_connections: intOrNull(r.unknown_connections),
        channels_formed: intOrNull(r.channels_formed),
        local_clients: intOrNull(r.local_clients),
        local_servers: intOrNull(r.local_servers),
        current_local: intOrNull(r.current_local),
        max_local: intOrNull(r.max_local),
        current_global: intOrNull(r.current_global),
        max_global: intOrNull(r.max_global),
      };
    }
    case "whowas_bundle":
      // P-0c — WHOWAS bundle. `not_found` discriminates the 406 case;
      // when true, historical fields are nil. cic owns the rendering
      // (single card per network, last-write-wins per /whowas).
      return validate(S_SessionWireWhowasBundlePayload, r);
    case "banlist_bundle":
      // #376/#1251 — channel LIST-MODE bundle. All entries ship (a list mode
      // is a set of rows). cic owns the rendering (single card per network,
      // last-write-wins per query).
      //
      // #1393d — this arm was hand-written for exactly one reason: the `mode`
      // absence tolerance, which coerced a missing key to `b`. `mode` is a
      // plain required string in the typespec, so no grappa running this code
      // omits it; the only peer that could was one predating #1251, and the
      // protocol floor is what reports that peer now. With the coercion gone
      // the arm had nothing left the schema does not already say — including
      // the per-element strictness, since `walkArray` drops the WHOLE array on
      // one bad element exactly as the hand loop did. So it calls `validate`,
      // and `narrowBanlistEntry` went with it.
      return validate(S_SessionWireBanlistBundlePayload, r);
    case "links_bundle":
      // #238 — LINKS topology bundle. All server nodes ship (a topology is a
      // set). ANY malformed element drops the whole bundle. An EMPTY entries
      // array is valid (the restricted/hidden-topology signal) — the modal
      // renders the empty state.
      //
      // #1393d — this arm's last tolerance survived a whole slice on a
      // distinction that does not exist. `mask` is `String.t() | nil`, and
      // that was read as "the field is optional": it is not. `| nil` is about
      // the VALUE, and the null is a real one carrying real meaning (`null` ⇒
      // the bare full-mesh request, non-null ⇒ "no server matches <mask>").
      // Whether the KEY may be MISSING is a different axis entirely, and the
      // generated schema answers it — `mask` is required, exactly like
      // `banlist_bundle.mode`, so no grappa running this code omits it and a
      // peer old enough to is the protocol floor's business.
      //
      // `validate` keeps the value axis untouched (`{u: ["s", "z"]}` accepts
      // the null) while closing the key axis, so the whole hand arm was the
      // tolerance and nothing else. `narrowLinksEntry` went with it —
      // `walkArray` drops the WHOLE array on one bad element, as it did.
      return validate(S_SessionWireLinksBundlePayload, r);
    case "joined":
    case "join_failed":
    case "kicked":
      // F1 (visitor-parity-and-nickserv cluster, 2026-05-15) — typed
      // window-state terminal events broadcast on user-topic ONLY by
      // `Session.Server.broadcast_window_state/2`, moved off the
      // per-channel topic to close the subscribe-then-broadcast race.
      // This is the LIVE carrier; the per-channel arms in
      // `subscribe.ts` see these kinds only as the cold snapshot.
      // REV-A H1 (2026-05-22) — shape
      // narrowing extracted to shared `narrowWindowStateEvent` so any
      // future server-side field add to e.g. `Session.Wire.kicked/4`
      // lands once at the helper. Dispatcher routes the typed result to
      // the per-store setters (setJoined/setFailed/setKicked) at the
      // call-site switch below (different store keys per topic boundary
      // → "reuses the verb, not the noun").
      return narrowWindowStateEvent(r);
    case "auto_away_debounce_changed":
      // #348 — the auto-away preference moved. `null` (no preference) is a
      // MEANINGFUL value here, not a missing field, and the typespec says so:
      // `{u: ["i", "z"]}` admits it and rejects anything that is neither.
      return validate(S_UserSettingsWireAutoAwayDebounceChangedPayload, r);
    case "quit_part_reason_changed":
      // issue 2150 — same posture as the debounce above: `null` is a
      // MEANINGFUL value (the subject cleared the message), not a missing
      // field, so the schema admits it and rejects anything else.
      return validate(S_UserSettingsWireQuitPartReasonChangedPayload, r);
    case "auto_away_reason_changed":
      return validate(S_UserSettingsWireAutoAwayReasonChangedPayload, r);
    case "away_nick_suffix_changed":
      // #1894 — same posture again: `null` is the subject switching the
      // rename off, a MEANINGFUL value rather than a missing field.
      return validate(S_UserSettingsWireAwayNickSuffixChangedPayload, r);
    case "archive_changed":
      // UX-1 (2026-05-17) — server broadcasts after a successful PART
      // (channel moves into archive list). Single-field envelope: cic
      // re-fetches via `loadArchive(slug)` rather than tracking a
      // wire-side delta (small set; refresh is idempotent and survives
      // reconnect replay). For the DESTRUCTIVE
      // `DELETE /networks/:slug/archive/:target` path the server now
      // broadcasts `archive_purged` instead — see below (UX-7-B).
      return validate(S_ScrollbackWireArchiveChangedPayload, r);
    case "archive_purged":
      // UX-7-B (2026-05-22) — server broadcasts after a successful
      // DELETE /networks/:slug/archive/:target. Two fields: the slug
      // (refresh the archive list, same as archive_changed) AND the
      // target (invalidate `scrollbackByChannel[channelKey(slug,
      // target)]` so the pre-delete rows don't ghost in the live Solid
      // store on re-JOIN — refreshScrollback's `?after=cursor` fetch
      // is past every deleted row, masking the gap without the purge).
      return validate(S_ScrollbackWireArchivePurgedPayload, r);
    case "directory_progress":
      return validate(S_SessionWireDirectoryProgressPayload, r);
    case "directory_complete":
      return validate(S_SessionWireDirectoryCompletePayload, r);
    case "directory_failed":
      return validate(S_SessionWireDirectoryFailedPayload, r);
    case "recover_progress":
      // #581 — one recovery-step transition. `step` + `status` are closed
      // sets, narrowed strictly (an unknown value drops this ONE presentational
      // ping, not the terminal result). `reason` is a nullable string (cic
      // localizes it in RecoverModal).
      //
      // #1393 — and that last sentence is why this arm does NOT move onto
      // `S_SessionWireRecoverProgressPayload`. The typespec CLOSES `reason`
      // over four tokens; this boundary takes any string, deliberately, so a
      // BEAM that adds a fifth cannot make an older cic drop the ping
      // (unknown-is-never-fatal, #447). The census only sees that once its
      // matrix swaps a VALUE instead of a type — `reason/swap`.
      if (
        typeof r.network !== "string" ||
        (r.step !== "identify" &&
          r.step !== "register" &&
          r.step !== "nick" &&
          r.step !== "recover" &&
          r.step !== "release") ||
        (r.status !== "running" && r.status !== "ok" && r.status !== "failed") ||
        (r.reason !== null && typeof r.reason !== "string")
      )
        return null;
      return {
        kind: "recover_progress",
        network: r.network,
        step: r.step,
        status: r.status,
        reason: r.reason as string | null,
      };
    case "recover_result":
      // #581 — terminal recovery outcome. `outcome` is a closed binary. `reason`
      // stays a nullable string (NOT hardened to the known token union) so an
      // additive server reason token can never drop a terminal result event
      // (unknown-is-never-fatal, #447); RecoverModal localizes the known tokens.
      //
      // #1393 — so this arm stays hand-written for the same reason as
      // `recover_progress` above, and here the stakes are a TERMINAL event
      // rather than a presentational ping.
      if (
        typeof r.network !== "string" ||
        (r.outcome !== "succeeded" && r.outcome !== "failed") ||
        (r.reason !== null && typeof r.reason !== "string")
      )
        return null;
      return {
        kind: "recover_result",
        network: r.network,
        outcome: r.outcome,
        reason: r.reason as string | null,
      };
    case "web_session_severed":
      // #630 — inbound-flood web-session sever. #1338 X-S14 widened `code`
      // from the closed literal to any string, the `recover_result.reason`
      // posture two arms up: the server may add a sever reason additively
      // (#447), the event's ACTION does not depend on the code, and dropping
      // a terminal event over an unrecognised value would strand the client
      // on a dead shell holding a revoked bearer. The value is carried
      // verbatim — it selects copy, nothing else. A missing or non-string
      // `code` is malformed rather than additive, and still drops.
      //
      // #1393 — `S_RateLimitWireWebSessionSeveredEvent` pins `code` to the
      // single literal `rate_limit_flood`, so validating against it would
      // undo #1338 exactly. The arm stays hand-written; `code/swap` in the
      // census is the row that says so.
      if (typeof r.code !== "string") return null;
      return { kind: "web_session_severed", code: r.code };
    default:
      return null;
  }
}

// E2E seam: `__cic_userTopicReady` (a Set<userName>) mirrors the LIVE
// user-topic subscription — stamped when a JOIN ack lands, cleared when
// the socket is torn down (logout) or rebuilt (rotation). Mirror of
// `__cic_dmListenerReady` (subscribe.ts): Playwright gates compose-driven
// specs on the stamp so the user-topic socket is subscribed BEFORE the
// test pushes /join (server's window_pending + join_failed broadcasts
// fastlane only to subscribed sockets — sub-50ms WS-ack races in suite
// context caused the pending/failed events to vanish, leaving cic with no
// sidebar pseudo-row). Clearing on teardown/rebuild keeps the stamp
// honest so a rotation gate awaits the RE-join, never a stale positive.
// Production never reads it.
function stampUserTopicReady(name: string): void {
  if (typeof window === "undefined") return;
  const w = window as Window & { __cic_userTopicReady?: Set<string> };
  if (!w.__cic_userTopicReady) w.__cic_userTopicReady = new Set();
  w.__cic_userTopicReady.add(name);
}

function clearUserTopicReady(): void {
  if (typeof window === "undefined") return;
  const w = window as Window & { __cic_userTopicReady?: Set<string> };
  // At most one user identity per tab, so a full clear is correct.
  w.__cic_userTopicReady?.clear();
}

moduleRoot(() => {
  // The user-topic Channel currently held on the live socket, or null
  // when logged out / mid-transition. Tracked (not a boolean guard) so a
  // rotation can `leave()` the orphaned prior Channel before re-joining —
  // see the effect body for the full why.
  let channel: Channel | null = null;
  // Previous bearer value, so this effect can distinguish a rotation
  // (a → b) / logout (a → null) from the initial run (null → a).
  let prevToken: string | null = null;

  createEffect(() => {
    // socket.ts rebuilds the Socket on EVERY token transition — login,
    // logout, AND rotation. The bearer rides the `authToken` subprotocol,
    // captured once at construction, so a fresh token means a fresh
    // Socket instance (socket.ts moduledoc); the prior user-topic Channel
    // is left bound to a now-dead socket on any transition off the value
    // we joined under. Leave it (H2 double-handler-leak parity with
    // subscribe.ts's rotation arm) and drop the ready stamp before
    // (re)joining on the rebuilt socket.
    //
    // Pre-fix this effect dedup'd on the derived IDENTITY (socketUserName)
    // and early-returned when the name was unchanged, so a rotation
    // (Phase 5 refresh / admin re-issue / same-visitor share-consume) with
    // a stable identity NEVER re-joined the rebuilt socket: every
    // user-topic event was lost AND every user/channel push verb rejected
    // "not connected" until a logout+reload. The SOCKET, not the identity,
    // is the resource this effect tracks. (#364 cicchetto S1)
    const t = token();
    if (prevToken !== null && prevToken !== t) {
      channel?.leave();
      channel = null;
      clearUserTopicReady();
    }
    prevToken = t;
    if (!t) return; // logged out — nothing to (re)join
    const name = socketUserName();
    if (name === null) return;
    // Re-join whenever no live Channel is held (initial join OR post-
    // rotation, where the leave above nulled it). A redundant effect
    // re-run under the same live socket is a no-op (channel already set).
    if (channel !== null) return;

    channel = joinUser(name, () => {
      stampUserTopicReady(name);
      // #370 — hydrate the keyword-highlight list on every user-topic
      // (re)join. Unlike the presence watch list (kept fresh by the server's
      // `notify_list` after-join push), the /hilight keyword list has NO
      // server broadcast — the only pull was `WatchlistsSettings` on open. So
      // without this, the in-message visual highlight for custom words stayed
      // dark until the operator opened the watch-lists settings section at
      // least once. onJoinOk fires on the initial join AND every auto-rejoin
      // (reconnect/rotation), so the list re-hydrates after a WS gap too. The
      // notify path already fired (server-side); this closes the visual gap.
      // Failure is logged, not swallowed — a next (re)join retries.
      void refreshHighlights().catch((err) =>
        console.warn("[userTopic] highlight-list hydrate failed", err),
      );
      // #385 — hydrate the user-defined alias map on every user-topic
      // (re)join, for the SAME reason as the highlight list above: the
      // aliases live in server user_settings with NO broadcast, and the
      // compose expander (parseSlash) reads them from the aliasList store.
      // Without this the store starts every page load at {} and a persisted
      // alias would read "unknown command" until the operator opened the
      // aliases settings sub-page at least once — defeating the whole point
      // of a server-synced, cross-device alias layer.
      void refreshAliases().catch((err) =>
        console.warn("[userTopic] alias-list hydrate failed", err),
      );
      // #868 — hydrate the notification prefs on every user-topic (re)join,
      // for the SAME reason as the two above: they live in server
      // user_settings with NO broadcast, and the live notify path
      // (`subscribe.ts` → `shouldNotify`) now reads them to decide the in-app
      // beep and the optimistic `document.title` bump. Without this the store
      // sits on `DEFAULT_NOTIFICATION_PREFS` for the whole session and a
      // subject who muted channel mentions would keep hearing them until they
      // opened the settings drawer. The signal defaults to the SERVER's own
      // defaults, so a failed hydrate degrades to "behaves like a subject who
      // never configured anything" — never to silence.
      void refreshNotificationPrefs().catch((err) =>
        console.warn("[userTopic] notification-prefs hydrate failed", err),
      );
      // issue 2430 — whatever encrypted messages waited while no cic was
      // connected sit in grappa's offline queue, and no push re-announces
      // them: drain every network's inbox on every (re)join.
      drainEveryNetwork();
      // issue 2430 X1 — the same for a pairing channel: its doorbells are
      // not replayed either, and a handshake would wait out the TTL.
      catchUpPairings();
      // issue 2486 — and for the requests to start an encrypted chat: a
      // peer's asking, or the answer to ours, that came while no cic was
      // connected is waiting on grappa, and no push re-announces it.
      reloadE2eRequests();
    });
    channel.on("event", (raw: unknown) => {
      const payload = narrowUserEvent(raw);
      if (payload === null) {
        // Malformed payload: kind missing/unknown OR a required field
        // missing/wrong-typed. Server bug or proxy mangling — drop and
        // log so the operator can investigate without crashing the WS
        // handler. Pre-fix `as WireUserEvent` cast would have let this
        // reach the dispatch arm and either crash a setter or corrupt
        // store state silently.
        // #1393d — and SAY so. A `console.warn` is a signal for whoever has
        // devtools open, which on a phone PWA is nobody; strict narrowing
        // made drops routine enough that silence is the failure mode.
        noteWireDrop(raw);
        console.warn("[userTopic] dropped malformed payload", raw);
        return;
      }
      switch (payload.kind) {
        case "channels_changed":
          refetchChannels();
          return;

        case "query_windows_list":
          setQueryWindowsByNetwork(parseWindowsMap(payload.windows));
          return;

        case "mentions_bundle": {
          // C8.1 — back-from-away mentions window. Wire the bundle
          // into the mentionsWindow store and auto-focus the mentions
          // pseudo-window so the user sees the aggregation immediately
          // on return. Focus-rule: this IS a user-action-driven event
          // (returning from away) so auto-focus is appropriate here
          // (unlike DM auto-open).
          const bundle = {
            network_slug: payload.network,
            away_started_at: payload.away_started_at,
            away_ended_at: payload.away_ended_at,
            away_reason: payload.away_reason,
            messages: payload.messages,
            // issue 2419 — absent from a pre-41 server, which never truncated.
            truncated: payload.truncated ?? false,
          };
          setMentionsBundle(payload.network, bundle);
          const currentSel = untrack(selectedChannel);
          if (
            !currentSel ||
            currentSel.networkSlug !== payload.network ||
            currentSel.kind !== "mentions"
          ) {
            setSelectedChannel({
              networkSlug: payload.network,
              channelName: "",
              kind: "mentions",
            });
          }
          return;
        }

        case "away_confirmed":
          // C8.3 — away visual indicator. Server broadcasts
          // away_confirmed with state: "away" | "present" on both set
          // and cancel paths. Update the awayByNetwork signal so the
          // Sidebar can show the 💤 badge (#276).
          //
          // #268 — this arm does NOT clear the mentions bundle. It used to
          // (`clearMentionsBundle` on state === "away"), but `away_confirmed`
          // is emitted only on the upstream 305/306 numeric echo
          // (event_router.ex), a different-latency channel than the
          // SYNCHRONOUS return-from-away `mentions_bundle` broadcast. Under
          // bahamut fake-lag a going-away's delayed 306 could arrive AFTER a
          // subsequent return's bundle and wrongly wipe it (the
          // "0 messages in 0 channels" bug). The clear-on-going-away lifecycle
          // now lives in compose.ts's `/away` (set) handler — causally ordered
          // with the user's own commands, so a fresh bundle can never be
          // clobbered by a stale echo. See docs/DESIGN_NOTES.md 2026-07-16.
          setAwayState(payload.network, payload.state === "away");
          return;

        case "own_nick_changed":
          // BUG1-FIX: the live IRC nick may differ from the credential's
          // configured nick after NickServ ghost recovery or an explicit
          // /nick. Patch the in-memory networks list so the DM-listener
          // loop and the query-window own-nick skip see the correct
          // nick immediately — no REST round-trip needed. The `joined`
          // set deduplication in subscribe.ts means the DM-listener
          // createEffect will re-run and subscribe to the new own-nick
          // topic on the next tick.
          mutateNetworkNick(payload.network_id, payload.nick);
          return;

        case "isupport_changed":
          // #216 — seed the per-network capability table the /mode modal
          // reads. Live edge (005 mid-session) + cold snapshot (per-channel
          // after-join) both flow here; last-write-wins idempotent. Fold
          // the flat wire fields into the nested store shape.
          seedIsupport(payload.network_id, isupportEntryFromWire(payload));
          return;

        case "umode_changed":
          // #229 — seed the per-network umode set the /mode <nick> modal
          // reads. Live edge (221 RPL_UMODEIS + self-MODE echoes) + cold
          // snapshot (user-topic after-join) both flow here; last-write-wins
          // idempotent.
          seedUmodes(payload.network_id, payload.modes);
          return;

        case "session_identity_changed":
          // #388 — seed the per-network services-identity verdict the
          // registration + recover launchers gate on. Live edge (any of the
          // server's identity sources) + cold snapshot (user-topic
          // after-join) both flow here; last-write-wins idempotent.
          seedIdentity(payload.network_id, payload.identified, payload.account);
          return;

        case "supported_umodes_changed":
          // #249 — seed the per-network SUPPORTED umode set the /umode modal
          // reads for its AVAILABLE toggles. Live edge (004 RPL_MYINFO) + cold
          // snapshot (user-topic after-join) both flow here; last-write-wins
          // idempotent.
          seedSupportedUmodes(payload.network_id, payload.modes);
          return;

        case "connection_state_changed":
          // Codebase review 2026-05-08 cross-infra H1: T32
          // disconnect/connect/mark_failed transitions emit this event
          // on the user-level topic. Refetch /networks so the UI sees
          // the updated `connection_state` / `connection_state_reason` /
          // `connection_state_changed_at` fields immediately on the
          // initiating tab AND on any sibling tab logged in to the
          // same account.
          //
          // REV-J M15: the prior standalone `home_network_state_changed`
          // arm folded into this payload as `:network`. HomePane patches
          // its row from the same event, eliminating the temporal window
          // where Sidebar saw the new state but HomePane hadn't yet.
          patchHomeNetwork(payload.network);
          // issue 2059 — hand selection.ts the transition from the EVENT,
          // before the refetch below. Bucket D used to derive the transition
          // by sampling `networks()`, i.e. the answer to that refetch, and a
          // sample cannot see a park shorter than one round-trip: on
          // `/reconnect` the GET issued here can be answered after the
          // credential already reads `connected` again, so the sampled
          // sequence is ["connected", "connected"] and the home redirect is
          // silently lost. Same move as `patchHomeNetwork` on the line above,
          // for the same reason and against the same window.
          noteConnectionState(payload.network_slug, payload.to);
          refetchNetworks();
          // #100 — clear a stuck "reconnecting…" badge on a SETTLED state.
          // The badge is set on connection_progress "connecting" and cleared
          // on "connected" (001). But a reconnect that ends terminally —
          // k-line / permanent-SASL → connection_state :failed, or an
          // operator /disconnect → :parked — never emits "connected", so
          // without this the badge would stay stuck. `parked`/`failed` are
          // non-connecting settled states, so clear the overlay. `connected`
          // is already cleared by connection_progress; clearing here too is
          // idempotent.
          if (payload.to === "parked" || payload.to === "failed") {
            setReconnecting(payload.network_slug, false);
          }
          return;

        case "network_detached":
          // issue 2219 — this network is no longer one of the account's.
          // BOTH refetches, because the two surfaces come from different
          // endpoints: the sidebar and its channel trees hang off
          // `GET /networks`, the `$home` rows off the `/me` envelope, and
          // dropping a network takes a row out of each. `refetchNetworks`
          // alone would leave `$home` offering a `[Reconnect]` chip for a
          // network whose every route now answers the iso 404.
          //
          // Nothing is mutated locally on the way past. cic never
          // originates state: the row goes when the two reads say it is
          // gone, which is also what makes this arm correct in the tab that
          // did NOT initiate the detach — the only tab that needs it.
          //
          // No selection rescue here, and none is missing. A detach of a
          // LIVE network parks it first, and that transition drives
          // `noteConnectionState` above into selection.ts's bucket-D
          // redirect; a detach of an already-parked one cannot be the
          // window in view, because #1985 took parked networks out of the
          // sidebar.
          refetchUser();
          refetchNetworks();
          return;

        case "network_attached":
          // issue 2219 — the same two reads, for the opposite direction.
          // The detach announcing itself while the attach stayed silent was
          // worse than neither announcing: a tab that did not initiate the
          // re-attach kept the network under `available_networks` with no
          // attached row, one tap from a request the server would refuse.
          //
          // `connection_state_changed` does not cover it. That arm fires on
          // the `:parked → :connected` flip and refetches `GET /networks`
          // alone, so the `$home` rows — which come from the `/me` envelope
          // — never move.
          refetchUser();
          refetchNetworks();
          return;

        case "window_pending":
          // CP17 — server-driven `:pending` window-state origination.
          // Server's `record_in_flight_join/2` writes
          // `window_states[ch] = :pending` and broadcasts on
          // `Topic.user/1` (NOT per-channel — chicken-and-egg: cic
          // only joins the per-channel topic AFTER seeing :pending
          // here). subscribe.ts:425's pre-subscribe loop re-runs on
          // the windowStateByChannel signal change and joins the
          // per-channel WS topic so the subsequent typed `joined` /
          // `join_failed` / `kicked` events land. Pre-CP17 cic mutated
          // the same store optimistically from compose.ts:210, which
          // was a parallel client-side state machine — closed by this
          // arm.
          setPending(channelKey(payload.network, payload.channel));
          return;

        case "window_invited":
          // #78 — inbound INVITE to a not-joined channel. Server's
          // apply_effects([{:invited, ch, inviter}]) writes
          // window_states[ch] = :invited and broadcasts here on Topic.user/1
          // (NOT per-channel — chicken-and-egg, same as window_pending
          // above). The pre-subscribe loop in subscribe.ts re-runs on the
          // windowStateByChannel signal change and joins the per-channel
          // topic so the persisted INVITE row lands in the channel buffer.
          //
          // #902 — still NO auto-focus, but the surface changed: the greyed
          // tab is gone and `errorBanners.ts` derives a dismissable top
          // banner with a [Join] action off this state. This arm remains the
          // single state owner ("derive, don't duplicate") — the banner
          // registry only projects it.
          setInvited(channelKey(payload.network, payload.channel), payload.inviter);
          return;

        case "window_invite_declined":
          // #976 — the invite was REFUSED (this device's × or another
          // device's). The server has already dropped the `:invited` window,
          // so mirroring it is a pure drop: `forceParted` clears the state
          // map + `invitedByChannel`, the banner registry stops deriving the
          // entry, and the cold-subscribe backfill has nothing left to
          // re-emit — which is the whole point of the issue.
          //
          // `forceParted`, not `setParted`: the #495 stale-echo guard makes
          // `setParted` a no-op on a "pending" key, and a decline racing a
          // JOIN the operator started elsewhere must still clear the invite
          // state it names. The server refuses to decline anything that is
          // not `:invited`, so this event never arrives for a live window.
          forceParted(channelKey(payload.network, payload.channel));
          return;

        case "dcc_offer":
          // issue 2089 — a peer offered a file and the server is HOLDING it,
          // awaiting explicit consent. Nothing was dialled and nothing was
          // stored; mirroring it is the whole client-side job, and the
          // consent surface derives off this store ("derive, don't
          // duplicate" — the #902 posture for the invite banner).
          //
          // Deliberately NOT routed into `windowStateByChannel`: an offer is
          // placed in a window, it is not one. `payload.channel` is where to
          // render it and is frequently `$server`.
          holdDccOffer({
            network: payload.network,
            channel: payload.channel,
            offer_id: payload.offer_id,
            from: payload.from,
            filename: payload.filename,
            size: payload.size,
          });
          return;

        case "dcc_offer_resolved":
          // The offer left the server's held set — this device's click,
          // another device's, or the hold running out. A pure drop either
          // way: the server has already applied it, and the cold-subscribe
          // backfill has nothing left to re-emit. `resolution` is copy, not
          // control flow, which is why one event covers all three.
          resolveDccOffer(payload.offer_id);
          return;

        case "whois_bundle": {
          // C2 — WHOIS reply complete (server's 318 RPL_ENDOFWHOIS).
          // #606 — the server marks the request origin (`source`), so the two
          // card stores stay disjoint by construction:
          //   * single-slot scrollback card (whoisCard) — `user` bundles ONLY,
          //     so a rail auto-fetch NEVER forges or clobbers the /whois card.
          //     Renders INLINE in the window the user typed /whois from (spec
          //     #2); we don't switch focus.
          //   * per-nick rail cache (railWhois) — its own `rail` bundles AND a
          //     `user` bundle for the nick the rail is CURRENTLY showing (a
          //     free refresh — turns the shared-whois_pending collision into a
          //     cache hit, not a race).
          // An absent source (pre-#606 server, replayed payload) was already
          // normalized to "user" by the narrower, so this is exhaustive.
          const { kind: _omit, ...bundle } = payload;
          if (bundle.source === "user") {
            setWhoisBundle(payload.network, bundle);
          }
          if (bundle.source === "rail" || railIsShowingNick(payload.network, payload.target)) {
            ingestRailWhois(payload.network, payload.target, bundle);
          }
          return;
        }

        case "whois_avatar_ready":
          // M3b — patch the open /whois card's avatar_url if it's still
          // showing this nick; silent no-op otherwise (see whoisCard.ts).
          patchWhoisAvatarUrl(payload.network, payload.nick, payload.avatar_url);
          return;

        case "e2e_message":
          // issue 2430 — the push says a message is queued; the drain takes
          // it from the inbox, in id order with anything else waiting, so a
          // missed push never leaves a gap (encryptedChats.ts).
          void drainE2e(payload.network).catch((err: unknown) =>
            console.warn(`[userTopic] encrypted inbox drain failed on ${payload.network}`, err),
          );
          return;

        case "e2e_send_dropped":
          // issue 2430 — grappa accepted a message and dropped it unsent:
          // its row turns failed, or, for an order's ACK, the loss is logged
          // (encryptedChats.ts). Every client of the user hears it; the one
          // that sent the message is the one that knows its msgid.
          void encryptedSendDropped(payload.network, payload.msgid, payload.reason).catch(
            (err: unknown) =>
              console.warn(
                `[userTopic] could not mark a dropped encrypted message on ${payload.network}`,
                err,
              ),
          );
          return;

        case "e2e_pairing_message":
        case "e2e_pairing_closed":
          // issue 2430 X1 — to the pairing channel this tab holds for that
          // rendezvous, if any; every device of the account hears both
          // (lib/e2e/pairing.ts). Issue 2537: either ends that rendezvous's
          // offer, a message meaning someone joined it.
          deliverPairingEvent(payload);
          pairingOfferEnded(payload.rendezvous_id);
          return;

        case "e2e_pairing_offered":
          // issue 2537 — a pairing another device offers: a banner on a
          // browser that can take it (lib/e2e/pairingOffer.ts).
          deliverPairingOffer(payload);
          return;

        case "e2e_request":
          // issue 2486 — every device of the account mirrors it; an
          // `accepted` one opens the encrypted chat here (lib/e2eRequests.ts).
          putE2eRequest(payload.network, payload.request);
          return;

        case "e2e_request_gone":
          dropE2eRequest(payload.network, payload.id);
          return;

        case "names_reply": {
          // #140 — /names roster complete (server's 366 RPL_ENDOFNAMES,
          // gated on a pending /names). Replace any prior roster for this
          // network; NamesModal (mounted in Shell) renders the grouped,
          // scrollable, dismissable modal for the active network. No focus
          // change — the operator is already on the issuing window.
          const { kind: _omit, ...reply } = payload;
          setNamesReply(payload.network, reply);
          return;
        }

        case "who_reply": {
          // #169 — /who roster complete (server's 315 RPL_ENDOFWHO, gated on
          // a pending /who). Replace any prior roster for this network;
          // WhoModal (mounted in Shell) renders the per-user table for the
          // active network. No focus change — the operator is already on the
          // issuing window.
          const { kind: _omit, ...reply } = payload;
          setWhoReply(payload.network, reply);
          return;
        }
        case "server_reply": {
          // #127 — /info|/version|/motd reply complete. Replace any prior
          // reply for this network; ServerReplyModal (mounted in Shell)
          // renders the scrollable line list for the active network. No
          // focus change — the operator stays on the issuing window.
          const { kind: _omit, ...reply } = payload;
          setServerReply(payload.network, reply);
          return;
        }

        case "bundle_hash":
          // CP23 S4 B5 — server pushes the deployed cic bundle hash on
          // user-topic join + on every cic-bundle-changed broadcast.
          // bundleHash.ts compares against bootBundleHash (the hash
          // baked into the page the browser loaded); mismatch shows the
          // refresh banner. No focus change — banner is a passive cue.
          // #292 — the deployed semver rides alongside the hash so the
          // banner can show "current X → available Y".
          setServerBundleHash(payload.hash);
          setServerBundleVersion(payload.version);
          return;

        case "server_settings_changed":
          // UX-6-B2 (2026-05-21) — operator-visible server-settings
          // push. Hydrates the reactive `serverSettings()` signal so
          // ComposeBox / SettingsDrawer / PrivacyModal pick up the
          // new active host + caps without a page reload. After-join
          // snapshot AND admin-PUT fan-out both ride this arm — same
          // setter applies in both directions (last-write-wins,
          // idempotent). Destructure to drop the `kind` discriminator
          // — `applyServerSettings` takes the `ServerSettingsWirePayload`
          // shape (upload subtree + #324 http_host_aliases) so
          // structural-typing drift (e.g. a future log site reading
          // `raw.kind`) can't surprise the REST call site whose response
          // has no `kind`.
          applyServerSettings({
            upload: payload.upload,
            http_host_aliases: payload.http_host_aliases,
            auto_away: payload.auto_away,
          });
          return;

        case "no_such_nick":
          // issue 2497 — the nick is not on the network: a message waiting
          // for its keys fails now (encryptedChats.ts). The 401 itself is a
          // row in the open query, or on $server, like any numeric.
          void encryptedPeerAbsent(payload.network, payload.nick).catch((err: unknown) =>
            console.warn(
              `[userTopic] could not end the wait for ${payload.nick}'s keys on ${payload.network}`,
              err,
            ),
          );
          return;

        case "peer_away":
          // P-0b — standalone 301 RPL_AWAY ephemeral. Stored keyed
          // on (network slug, peer-nick lowercased); ScrollbackPane
          // mounts the banner only when the selected window matches.
          // Last-write-wins: re-/msg'ing the same away peer replaces
          // the prior message. No focus change.
          setPeerAway(payload.network, payload.peer, payload.message);
          return;

        case "lusers_bundle": {
          // P-0d / #248 — LUSERS bundle. Gated on the solicited-request
          // flag: applyLusersBundle surfaces the card ONLY when the
          // operator issued /lusers (markLusersRequested), consume-once.
          // The Bahamut connect-welcome auto-emit is never preceded by a
          // request → dropped silently, so it no longer floats a covering
          // card over the message view. No focus change either way.
          const { kind: _omit, network, ...snapshot } = payload;
          applyLusersBundle(network, snapshot);
          return;
        }

        case "whowas_bundle": {
          // P-0c — WHOWAS bundle. Last-write-wins per-network. Renders
          // inline above the active window scrollback (mirrors WhoisCard).
          // No focus change: operator typed /whowas from the window
          // they're looking at; the card renders there. The 406
          // not_found case is folded into the same arm — cic renders a
          // "no history" surface from the boolean.
          const { kind: _omit, ...bundle } = payload;
          setWhowasBundle(payload.network, bundle);
          return;
        }

        case "banlist_bundle": {
          // #376/#1251 — list-mode bundle. Last-write-wins per-network; the
          // bundle's `mode` is what lets the modal tell "no reply yet for
          // +e" from "here is the +b list I asked for before". Renders
          // inline above the active window scrollback (mirrors WhoisCard/
          // WhowasCard). No focus change: operator typed /banlist from the
          // window they're looking at; the card renders there.
          const { kind: _omit, ...bundle } = payload;
          setBanlistBundle(payload.network, bundle);
          return;
        }

        case "links_bundle": {
          // #238 — LINKS topology complete (server's 365 RPL_ENDOFLINKS, gated
          // on a pending /links). Replace any prior topology for this network;
          // LinksModal (mounted in Shell, network-scoped) renders the
          // interactive SVG map for the active network. An empty bundle
          // (restricted topology) still fills the store → the modal opens to
          // the "hides topology" empty state. No focus change — the operator
          // stays on the issuing window. Mirror of who_reply.
          const { kind: _omit, ...reply } = payload;
          setLinksReply(payload.network, reply);
          return;
        }

        case "invite_ack":
          // P-0e + P-0f — append a synthetic row to the per-network
          // store keyed on target channel. ScrollbackPane's `rows()`
          // memo interleaves invite-ack entries into the $server
          // window timeline by wallclock `at`. No focus change —
          // server-window auto-yank would be antisocial.
          appendInviteAck(payload.network, payload.channel, payload.peer);
          return;

        case "joined":
          // F1 (visitor-parity-and-nickserv cluster, 2026-05-15) — this
          // is where a LIVE window-state transition arrives; the
          // per-channel topic no longer broadcasts it, per
          // `Session.Server.broadcast_window_state/2`. Same setter the
          // per-channel arm at `subscribe.ts` calls for the cold
          // snapshot; last-write-wins idempotent so both-paths arrival
          // on a cold reconnect is safe.
          setJoined(channelKey(payload.network, payload.channel));
          return;

        case "join_failed":
          // F1 — see `joined` arm above. Same shape + setter as
          // `subscribe.ts:313` per-channel arm.
          setFailed(channelKey(payload.network, payload.channel), payload.reason, payload.numeric);
          return;

        case "kicked":
          // F1 — see `joined` arm above. Same shape + setter as
          // `subscribe.ts` per-channel kicked arm.
          setKicked(channelKey(payload.network, payload.channel), payload.by, payload.reason);
          return;

        case "auto_away_debounce_changed":
          // #348 — mirror the server's own announcement so the settings
          // control converges across the subject's devices. cic never
          // originates this state; it only reflects what the server
          // stored, including for the write this device just made.
          applyAutoAwayDebounceFromWire(payload.auto_away_debounce_seconds);
          return;

        case "quit_part_reason_changed":
          // issue 2150 — mirror the server's announcement so the settings
          // input converges across the subject's devices. cic never
          // originates this state; the SERVER resolves the default when it
          // builds the QUIT/PART line, and this is only the display copy.
          applyQuitPartReasonFromWire(payload.quit_part_reason);
          return;

        case "auto_away_reason_changed":
          applyAutoAwayReasonFromWire(payload.auto_away_reason);
          return;

        case "away_nick_suffix_changed":
          // #1894 — display copy only. The SERVER decides whether to
          // rename and what to; cic never originates it.
          applyAwayNickSuffixFromWire(payload.away_nick_suffix);
          return;

        case "archive_changed":
          // UX-1 (2026-05-17) — PART moved a channel into archive.
          // Re-fetch via the existing `loadArchive(slug)` helper rather
          // than tracking the wire-side delta (the set is small and
          // refresh is idempotent + reconnect-safe).
          void loadArchive(payload.network_slug);
          return;

        case "archive_purged":
          // UX-7-B (2026-05-22) — destructive archive-entry delete.
          // Order (purge → clearSeen → loadArchive) covers the common
          // happy path: invalidate the in-memory scrollback cache for
          // the target key, then drop the high-water mark so the next
          // refresh fetches from 0 (or the server-side read cursor)
          // rather than the pre-delete high-water, then refresh the
          // archive list so the modal/sidebar sections drop the row.
          //
          // Edge case (not defended): an `await listMessagesAfter(...)`
          // already in flight when this handler runs will resolve into
          // `appendToScrollback` + `recordSeen` on the just-purged
          // key, re-seeding the store from rows the server returned
          // BEFORE the delete landed. Practical likelihood is ~zero
          // because the DELETE response (204) is sent by the same
          // controller process that emits this broadcast, well before
          // any post-delete re-JOIN's refreshScrollback could fire.
          // Stale rows would be re-purged on the next archive_purged
          // anyway. Not worth the complexity to await/cancel.
          //
          // The cic-side `readCursor.cursors[key]` is INTENTIONALLY
          // NOT cleared here: (1) the server's `read_cursors` row was
          // already `ON DELETE SET NULL`'d by the migration FK; (2)
          // `applyJoinReply` no-ops on a null cursor so cic's signal
          // map retains the stale id; (3) sqlite AUTOINCREMENT is
          // monotonic, so any post-purge live message has id > stale
          // cursor and the next `refreshScrollback` returns the full
          // gap. Clearing client-side would break cross-device cursor
          // sync (settled reads on device A would vanish on device B
          // after the unrelated archive delete).
          {
            const key = channelKey(payload.network_slug, payload.target);
            purgeScrollback(key);
            clearSeen(key);
            // issue 2096 — and the server `unread_counts` SEED for the key
            // too, whose rows were just deleted. It is written by `/me` and
            // the join reply and by nothing else, so without this it stands
            // until the next cold load. Invisible until the archive launcher
            // grew a rollup badge that sums seed keys with no window behind
            // them; then it is a number with nothing left to read. Distinct
            // from the read cursor immediately below, which stays ON PURPOSE.
            clearServerSeedCount(key);
            void loadArchive(payload.network_slug);
          }
          return;

        case "directory_progress":
          void onDirectoryProgress(payload.network);
          return;
        case "directory_complete":
          void onDirectoryComplete(payload.network);
          return;
        case "directory_failed":
          void onDirectoryFailed(payload.network);
          return;

        case "connection_progress":
          // #100 — flip the per-network "reconnecting…" sidebar badge.
          // "connecting" (a Session (re)establishing the upstream socket)
          // shows it; "connected" (001 RPL_WELCOME) clears it. Presentational
          // overlay only — the durable connection_state is untouched.
          setReconnecting(payload.network, payload.state === "connecting");
          // #248 — a fresh connection attempt invalidates any pending
          // /lusers request for this network: the imminent registration
          // welcome LUSERS burst is unsolicited and must not consume a
          // stale flag left by a /lusers issued on a prior (now-dead)
          // session. Clear on the "connecting" edge (before the burst).
          if (payload.state === "connecting") clearLusersRequested(payload.network);
          // #474 B — refetch the per-network rows once the socket is truly up.
          // The LIVE `connection` facts (dialled server / port / TLS / +r
          // identified) reach the store ONLY via a full GET /networks, and the
          // only other trigger — connection_state_changed — fires at the DB
          // :parked → :connected flip, BEFORE the peer is captured (its refetch
          // returns connection: null). "connected" is 001 RPL_WELCOME: the peer
          // IS captured and (for SASL) +r is already set, so refetch now to fill
          // the server-window rail card. Without this the card is blank exactly
          // after a runtime reconnect — the #550 netsplit-triage case the card
          // exists for. GET /networks emits no connection_progress, so no loop.
          // Known follow-on (#474): a nick that gains +r LATER via post-connect
          // NickServ IDENTIFY (auth_method :nickserv_identify) reads
          // identified="no" until the next natural refetch — not chased here.
          if (payload.state === "connected") refetchNetworks();
          return;

        case "recover_progress":
          // #581 — mirror one recovery-step transition into the RecoverModal
          // progress store. The FIRST progress event OPENS the modal (cic never
          // originates state — the server drives); later ones accumulate/update
          // the step list.
          applyRecoverProgress(payload);
          return;

        case "recover_result":
          // #581 — terminal recovery outcome. No-ops when the modal is closed
          // (dismissed mid-flight / never opened this session — store patch
          // guard); otherwise sets the success/failure conclusion.
          applyRecoverResult(payload);
          return;

        // #247 — /notify watch list + presence. Full-list snapshot
        // (per-mutation broadcast + after-join push) and the live dot
        // updates; toast gating on `initial` lives in the store.
        case "notify_list":
          setNotifyList(payload.networks);
          return;

        case "presence_snapshot":
          applyPresenceSnapshot(payload.network_id, payload.nicks);
          return;

        case "presence_changed":
          applyPresenceChange(payload);
          return;

        case "presence_error":
          // Upstream rejected the watch registration (list full).
          // Error-styled toast via the presence toast queue
          // (review 2026-07-19 R2 — diagPush alone renders only behind
          // the cic_diag flag, i.e. never in production); the raw
          // numeric also landed as a $server notice row server-side.
          applyPresenceError(payload);
          diagPush(
            `notify: watch list full on network ${payload.network_id} — rejected: ${payload.detail}`,
          );
          return;

        case "web_session_severed":
          // #630 — the server severed this web session for inbound flooding
          // (the bearer revoke + socket close land right behind this event).
          // Two steps, ORDER-CRITICAL:
          //   (1) Latch the persistent flood flag FIRST, so it is already set
          //       when the re-login screen reads it. floodSever.ts is a plain
          //       module signal (NOT identity-scoped), so it survives the
          //       token-clear in step (2); it's cleared only on the next
          //       successful login (auth.ts).
          //   (2) Proactively drop local auth (clearLocalAuth → setToken(null)
          //       → the token signal goes null → RequireAuth bounces to
          //       /login, socket.ts disconnects the WS). This is the
          //       DETERMINISTIC drop-to-login signal — cic must NOT sit on a
          //       broken shell waiting for the WS reconnect-failure path to
          //       eventually notice the revoked bearer. clearLocalAuth's
          //       setToken(null) does NOT reset the flood flag (the clear is
          //       guarded on a non-null bearer), so the banner survives to be
          //       rendered on the login screen.
          // #1338 X-S14 — step (2) is UNCONDITIONAL, step (1) is the copy.
          // Only "rate_limit_flood" earns the dedicated flood banner; any
          // other sever reason still drops to login, on the generic
          // logged-out screen `Grappa.RateLimit.Wire` names as the fallback.
          if (payload.code === "rate_limit_flood") setSeveredForFlood(true);
          clearLocalAuth();
          return;

        default:
          assertNever(payload);
      }
    });
  });
});
